import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Navigation, RefreshCw, XCircle, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AddStopDialog } from '@/components/transport/AddStopDialog';
import { transportApi } from '@/services/transportApi';

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
export const STOP_BADGE = {
  PENDING: 'bg-gray-100 text-gray-600', ARRIVED: 'bg-amber-100 text-amber-700',
  DONE: 'bg-green-100 text-green-700', REJECTED: 'bg-red-100 text-red-700',
};

/** One stop row with live-add / rejection details (used by Running trips + My Tasks). */
export const StopInfo = ({ s }) => (
  <div className="min-w-0">
    <p className="text-xs font-medium truncate">
      <span className={s.kind === 'pickup' ? 'text-emerald-600' : 'text-red-600'}>{s.kind === 'pickup' ? '▲ Pickup' : '▼ Drop'}</span> {s.label}
      {s.for_section && <span className="ml-1 text-[10px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-1" data-testid={`stop-for-${s.stop_id}`}>For: {s.for_section}</span>}
      {s.added_live && <span className="ml-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">added by {s.added_by_name || 'staff'}</span>}
      {s.acceptance === 'AWAITING' && <span className="ml-1 text-[10px] text-blue-700" data-testid={`stop-awaiting-${s.stop_id}`}>· awaiting driver</span>}
    </p>
    {s.note && <p className="text-[11px] text-gray-500 truncate">Note: {s.note}</p>}
    {s.status === 'REJECTED' && (
      <div className="mt-1 text-[11px] text-red-700 space-y-1" data-testid={`stop-reject-info-${s.stop_id}`}>
        <p className="flex items-start gap-1"><XCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Rejected by {s.rejected_by || 'driver'}: {s.reject_reason_text || s.reject_transcript || 'voice note'}
            {s.reject_reason_text && s.reject_transcript && <span className="text-red-500"> · voice: “{s.reject_transcript}”</span>}</span>
        </p>
        {s.reject_audio_url && <audio controls src={s.reject_audio_url} className="h-8 w-full max-w-xs" data-testid={`stop-reject-audio-${s.stop_id}`} />}
      </div>
    )}
  </div>
);

export const RunningTrips = () => {
  const [trips, setTrips] = useState(null);
  const [addFor, setAddFor] = useState(null);
  const load = useCallback(async () => {
    try { setTrips(await transportApi.runningTrips()); } catch { setTrips([]); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  return (
    <div className="space-y-3" data-testid="running-trips">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Vehicles on the road now. Add a pickup or drop — the driver will accept or reject it.</p>
        <Button size="sm" variant="ghost" onClick={load} data-testid="running-trips-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      {trips && trips.length === 0 && <Card className="border-0 shadow-sm"><CardContent className="p-6 text-center text-sm text-gray-400" data-testid="running-trips-empty">No trips are in transit right now.</CardContent></Card>}
      {(trips || []).map((t) => {
        const stops = t.stops || [];
        const done = stops.filter((s) => ['DONE', 'REJECTED'].includes(s.status)).length;
        return (
          <Card key={t.id} className="border-0 shadow-sm border-l-4 border-l-teal-500" data-testid={`running-trip-${t.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm flex items-center gap-1"><Truck className="w-4 h-4 text-teal-600" />{t.vehicle_label} · {t.driver_name || '—'}</p>
                  <p className="text-xs text-gray-500 truncate">{t.route_summary}</p>
                  {t.eta ? <p className="text-xs text-teal-700 font-medium flex items-center gap-1" data-testid={`running-trip-eta-${t.id}`}><Navigation className="w-3 h-3" />ETA ~{fmtTime(t.eta.eta_at)} · ~{t.eta.remaining_km} km left</p>
                    : <p className="text-xs text-gray-400">Started {fmtTime(t.start_time)} · awaiting live location</p>}
                </div>
                <Button size="sm" onClick={() => setAddFor(t)} className="bg-amber-500 hover:bg-amber-600 shrink-0" data-testid={`running-trip-add-stop-${t.id}`}><Plus className="w-4 h-4 mr-1" />Add stop</Button>
              </div>
              <div className="border-t pt-2 space-y-1">
                <p className="text-[11px] text-gray-500">Stops {done}/{stops.length} closed</p>
                {stops.map((s, i) => (
                  <div key={s.stop_id || i} className="flex items-start justify-between gap-2 bg-gray-50 rounded px-2 py-1.5" data-testid={`running-trip-stop-${t.id}-${i}`}>
                    <StopInfo s={s} />
                    <Badge className={STOP_BADGE[s.status || 'PENDING']}>{s.status || 'PENDING'}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
      <AddStopDialog trip={addFor} onClose={() => setAddFor(null)} onDone={() => { setAddFor(null); load(); }} />
    </div>
  );
};

export default RunningTrips;
